import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Ban, KeyRound, Pause, RefreshCw, RotateCcw, ShieldOff } from 'lucide-react'
import { usersApi } from '@/api/users'
import { Button, Modal } from '@/components/ui'
import type { UserListItem } from '@/types'

type AccessAction = 'reset_portal_otp' | 'reset_mfa' | 'suspend' | 'restore' | 'cancel'

interface Props {
  user: UserListItem | null
  isOpen: boolean
  onClose: () => void
  onResetPassword: (user: UserListItem) => void
}

const actionCopy: Record<AccessAction, { title: string; impact: string; confirm: string; danger?: boolean }> = {
  reset_portal_otp: {
    title: 'איפוס קוד חד־פעמי',
    impact: 'כל קוד פורטל פעיל יבוטל וקוד חדש יישלח לערוץ המאומת.',
    confirm: 'בטל ושלח קוד חדש',
  },
  reset_mfa: {
    title: 'איפוס אימות דו־שלבי',
    impact: 'כל גורמי MFA/TOTP יוסרו. המשתמש ינותק ויידרש להירשם מחדש.',
    confirm: 'אפס MFA ונתק סשנים',
    danger: true,
  },
  suspend: {
    title: 'הקפאת משתמש',
    impact: 'הגישה תיחסם מיד, אך ניתן יהיה לשחזר את המשתמש.',
    confirm: 'הקפא משתמש',
    danger: true,
  },
  restore: {
    title: 'שחזור משתמש',
    impact: 'המשתמש יחזור למצב פעיל והחסימה ב־Auth תוסר.',
    confirm: 'שחזר משתמש',
  },
  cancel: {
    title: 'ביטול משתמש',
    impact: 'המשתמש יהפוך ללא פעיל וגישתו תיחסם. הנתונים לא יימחקו.',
    confirm: 'בטל משתמש',
    danger: true,
  },
}

export default function UserAccessActions({ user, isOpen, onClose, onResetPassword }: Props) {
  const [action, setAction] = useState<AccessAction | null>(null)
  const [reason, setReason] = useState('')
  const queryClient = useQueryClient()

  const close = () => {
    setAction(null)
    setReason('')
    onClose()
  }

  const mutation = useMutation({
    mutationFn: async () => {
      if (!user || !action) throw new Error('User or action missing')
      return usersApi.manageAccess({ target_user_id: user.id, action, reason: reason.trim() })
    },
    onSuccess: () => {
      toast.success('הפעולה בוצעה ונרשמה ביומן')
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-org-roles'] })
      close()
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'הפעולה נכשלה'),
  })

  if (!user) return null
  const selected = action ? actionCopy[action] : null
  const inactive = user.user_status !== 'active'

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={selected?.title ?? `ניהול ${user.name || user.email}`}
      footer={selected ? (
        <>
          <Button variant="secondary" onClick={() => { setAction(null); setReason('') }} disabled={mutation.isPending}>חזרה</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={reason.trim().length < 5}>
            {selected.confirm}
          </Button>
        </>
      ) : undefined}
    >
      {selected ? (
        <div dir="rtl" className="space-y-4">
          <p className={`rounded-lg border p-3 text-sm ${selected.danger ? 'border-red-500/30 bg-red-500/10 text-red-300' : 'border-amber-500/30 bg-amber-500/10 text-amber-300'}`}>
            {selected.impact}
          </p>
          <div>
            <label className="mb-1 block text-sm text-ink-secondary">סיבה לביצוע</label>
            <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} placeholder="נדרשים לפחות 5 תווים" className="w-full rounded-lg border border-navy-border bg-navy-card px-3 py-2 text-sm text-ink-primary" />
          </div>
        </div>
      ) : (
        <div dir="rtl" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button type="button" onClick={() => onResetPassword(user)} className="flex items-center gap-2 rounded-xl border border-navy-border bg-navy-dark p-3 text-right text-sm text-ink-primary"><KeyRound className="h-4 w-4 text-indigo-300" />איפוס סיסמה</button>
          <button type="button" onClick={() => setAction('reset_portal_otp')} className="flex items-center gap-2 rounded-xl border border-navy-border bg-navy-dark p-3 text-right text-sm text-ink-primary"><RefreshCw className="h-4 w-4 text-indigo-300" />איפוס קוד חד־פעמי</button>
          <button type="button" onClick={() => setAction('reset_mfa')} disabled={!user.auth_user_id} className="flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-right text-sm text-red-300 disabled:opacity-40"><ShieldOff className="h-4 w-4" />איפוס MFA/TOTP</button>
          {inactive ? (
            <button type="button" onClick={() => setAction('restore')} className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-right text-sm text-emerald-300"><RotateCcw className="h-4 w-4" />שחזור משתמש</button>
          ) : (
            <button type="button" onClick={() => setAction('suspend')} className="flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-right text-sm text-amber-300"><Pause className="h-4 w-4" />הקפאת משתמש</button>
          )}
          <button type="button" onClick={() => setAction('cancel')} className="flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-right text-sm text-red-300 sm:col-span-2"><Ban className="h-4 w-4" />ביטול משתמש ללא מחיקת נתונים</button>
        </div>
      )}
    </Modal>
  )
}
